import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { EventRecord } from "./queryEventsSlice";
import { saveSetting } from "@src/utils/storage.utils";

export type PaneSizeKey = "eventsInventorySplit" | "mainRightSplit" | "mapFeedSplit" | "inventoryEditSplit";

const PANE_STORAGE_KEYS: Record<PaneSizeKey, string> = {
	eventsInventorySplit: "portalEventsInventorySplit",
	mainRightSplit: "portalMainRightSplit",
	mapFeedSplit: "portalMapFeedSplit",
	inventoryEditSplit: "portalInventoryEditSplit",
};

const readPaneSize = (key: string): number | null => {
	const raw = localStorage.getItem(key);
	if (!raw) return null;
	const value = Number(raw);
	return Number.isFinite(value) ? value : null;
};

export enum LayoutActionEnum {
	CompactView = "CompactView",
	StandardView = "StandardView",
	FullScreenView = " FullScreenView",
	EventsFullScreen = "EventsFullScreen",
	InventoryFullScreen = "InventoryFullScreen",
}

export enum ManageInventoryTabEnum {
	Autopricer = "Autopricer",
	ManageListing = "ManageListing",
}

export enum SelectedManageInventoryTabEnum {
	Basic = "Basic",
	Advanced = "Advanced",
}

export enum PricingTabEnum {
	Events = "Events",
	GlobalInventory = "GlobalInventory",
}

export enum HighlightedSectionEnum {
	ListingInfo = "Listing Info",
	ShareUnshare = "Share/Unshare",
	Tickets = "Tickets",
	InternalNotes = "Internal Notes",
	ExternalNotes = "External Notes",
	AttachPDF = "Attach PDF's",
	SchedulePrice = "Schedule Price",
	FlashSale = "Flash Sale",
	PurchaseInformation = "Purchase Information",
	AddTag = "Add Tag",
	SplitListing = "Split Listing",
}

export enum SalesHighlightedSectionEnum {
	UpdateCost = "Update Cost",
	Tickets = "Tickets",
	InternalNotes = "Internal Notes",
	AttachPDF = "Attach PDF's",
	PaymentHistory = "Payment History",
	Invoice = "Invoice",
}

interface ISelectedEvent {
	event: EventRecord | null;
	showComingSoon: boolean;
	inventoryHasChanges: boolean;
	layoutAction: LayoutActionEnum;
	isEditListingEnabled: boolean;
	isMapAndFeedEnabled: boolean;
	manageInventoryTab: ManageInventoryTabEnum;
	selectedInventoryTab: SelectedManageInventoryTabEnum;
	viewSeasonLowestComparables: boolean;
	inventoryItemIds: Array<number>;
	highlightedSection: string | null;
	openTagModalWithId: number | null;
	pricingTab: PricingTabEnum;
	savedEventsEvent: EventRecord | null;
	savedEventsInventoryItemIds: Array<number>;
	savedEventsIsEditListingEnabled: boolean;
	eventsInventorySplit: number | null;
	mainRightSplit: number | null;
	mapFeedSplit: number | null;
	inventoryEditSplit: number | null;
}

const initialState: ISelectedEvent = {
	event: null,
	showComingSoon: false,
	inventoryHasChanges: false,
	layoutAction: (localStorage.getItem("portalLayoutAction") as LayoutActionEnum) ?? LayoutActionEnum.StandardView,
	isEditListingEnabled: false,
	isMapAndFeedEnabled: localStorage.getItem("portalIsMapAndFeedEnabled") !== "false",
	manageInventoryTab: ManageInventoryTabEnum.ManageListing,
	selectedInventoryTab: SelectedManageInventoryTabEnum.Basic,
	viewSeasonLowestComparables: false,
	inventoryItemIds: [],
	highlightedSection: null,
	openTagModalWithId: null,
	pricingTab: PricingTabEnum.Events,
	savedEventsEvent: null,
	savedEventsInventoryItemIds: [],
	savedEventsIsEditListingEnabled: false,
	eventsInventorySplit: readPaneSize(PANE_STORAGE_KEYS.eventsInventorySplit),
	mainRightSplit: readPaneSize(PANE_STORAGE_KEYS.mainRightSplit),
	mapFeedSplit: readPaneSize(PANE_STORAGE_KEYS.mapFeedSplit),
	inventoryEditSplit: readPaneSize(PANE_STORAGE_KEYS.inventoryEditSplit),
};

export const selectionSlice = createSlice({
	name: "selectionSlice",
	initialState,
	reducers: {
		setInventoryHasChanges: (state, action: PayloadAction<{ inventoryHasChanges: boolean }>) => {
			return {
				...state,
				...action.payload,
			};
		},
		setEvent: (state, action: PayloadAction<{ event: EventRecord | null }>) => {
			return {
				...state,
				...action.payload,
			};
		},
		setInventory: (
			state,
			action: PayloadAction<{
				inventoryItemIds?: Array<number>;
				layoutAction?: LayoutActionEnum;
				isEditListingEnabled?: boolean;
			}>,
		) => {
			const { layoutAction } = action.payload;
			const clearEventsSplit = layoutAction !== undefined;

			if (layoutAction) {
				saveSetting("portalLayoutAction", layoutAction, true);
			}

			if (clearEventsSplit) {
				saveSetting(PANE_STORAGE_KEYS.eventsInventorySplit, null, true);
			}

			return {
				...state,
				...action.payload,
				...(clearEventsSplit ? { eventsInventorySplit: null } : {}),
			};
		},
		setManageInventoryTab: (state, action: PayloadAction<ManageInventoryTabEnum>) => {
			state.manageInventoryTab = action.payload;
		},
		setSelectedInventoryTab: (state, action: PayloadAction<SelectedManageInventoryTabEnum>) => {
			state.selectedInventoryTab = action.payload;
		},
		reset: (state) => {
			state.event = null;
			state.inventoryItemIds = [];
		},
		removeInventoryId: (state, action: PayloadAction<number>) => {
			state.inventoryItemIds = state.inventoryItemIds.filter((x) => x != action.payload);
		},
		showComingSoon: (state, action: PayloadAction<{ showComingSoon: boolean }>) => {
			return {
				...state,
				...action.payload,
			};
		},
		setViewSeasonLowestComparables: (state, action: PayloadAction<boolean>) => {
			state.viewSeasonLowestComparables = action.payload;
		},
		setHighlightedSection: (state, action: PayloadAction<HighlightedSectionEnum | SalesHighlightedSectionEnum | null>) => {
			state.highlightedSection = action.payload;
		},
		setTagModalId: (state, action: PayloadAction<ISelectedEvent["openTagModalWithId"]>) => {
			state.openTagModalWithId = action.payload;
		},
		setIsMapMapAndFeedEnabled: (state, action: PayloadAction<boolean>) => {
			state.isMapAndFeedEnabled = action.payload;
			saveSetting("portalIsMapAndFeedEnabled", action.payload, true);
		},
		setPaneSize: (state, action: PayloadAction<{ pane: PaneSizeKey; value: number | null }>) => {
			const { pane, value } = action.payload;
			state[pane] = value;
			saveSetting(PANE_STORAGE_KEYS[pane], value === null ? null : String(value), true);
		},
		setPricingTab: (state, action: PayloadAction<PricingTabEnum>) => {
			if (action.payload === PricingTabEnum.GlobalInventory) {
				state.savedEventsEvent = state.event;
				state.savedEventsInventoryItemIds = state.inventoryItemIds;
				state.savedEventsIsEditListingEnabled = state.isEditListingEnabled;
				state.inventoryItemIds = [];
				state.isEditListingEnabled = false;
			} else if (action.payload === PricingTabEnum.Events && state.savedEventsEvent) {
				state.event = state.savedEventsEvent;
				state.inventoryItemIds = state.savedEventsInventoryItemIds;
				state.isEditListingEnabled = state.savedEventsIsEditListingEnabled;
				state.savedEventsEvent = null;
				state.savedEventsInventoryItemIds = [];
				state.savedEventsIsEditListingEnabled = false;
			}
			state.pricingTab = action.payload;
		},
		clearSavedEventsEvent: (state) => {
			state.event = state.savedEventsEvent;
			state.inventoryItemIds = state.savedEventsInventoryItemIds;
			state.isEditListingEnabled = state.savedEventsIsEditListingEnabled;
			state.savedEventsEvent = null;
			state.savedEventsInventoryItemIds = [];
			state.savedEventsIsEditListingEnabled = false;
		},
	},
});

export const selectedInventoryItemIds = (state: RootState) => state.pricing.selection.inventoryItemIds;
export const selectViewSeasonLowestComparables = (state: RootState) => state.pricing.selection.viewSeasonLowestComparables;
